import {
  type Dispatch,
  type FC,
  type PropsWithChildren,
  type SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import BasicModal from '__components/Modals/presets/BasicModal';
import ConfirmationModal from 'components/Modals/presets/ConfirmationModal';
import { toastVariant, useToast } from '__components/ToastProvider';
import TextInput from '__components/Inputs/TextInput';
import MultiSelect from '__components/Inputs/MultiSelect';
import { KizenTypography } from '__app/typography';
import { getOriginalError } from '__services/AxiosService';
import { getErrorMessage } from '__hooks/useErrors';
import ServiceAccountService from 'services/ServiceAccountService';
import { permissionGroupsQuery } from 'query/permission-group';
import { primaryTokenQuery, secondaryTokenQuery } from 'query/service-account';
import type { Option, SelectedServiceAccount } from './types';
import { DEFAULT_INPUT_DELAY } from 'utility/config';
import { useDebounce } from 'react-use';
import { getChosenBusiness } from 'store/authentication/selectors';
import { useSelector } from 'react-redux';
import { useFlashTransition } from '__hooks/useFlashState';
import { StyledAccountName, StyledDeletionNotice } from './styles';
import { TextEllipsisWithTooltip } from '__components/Kizen/Table';
import Loader from '__components/Kizen/Loader';

const emptyLongDash = '—';

type CreateModalProps = {
  setSelectedServiceAccount: Dispatch<SetStateAction<SelectedServiceAccount>>;
  refetch: () => void;
};

type ActionModalProps = {
  selectedServiceAccount: SelectedServiceAccount;
  setSelectedServiceAccount: Dispatch<SetStateAction<SelectedServiceAccount>>;
  refetch: () => void;
};

type ViewModalProps = {
  selectedServiceAccount: SelectedServiceAccount;
  setSelectedServiceAccount: Dispatch<SetStateAction<SelectedServiceAccount>>;
};

const closeModal = (
  setter: Dispatch<SetStateAction<SelectedServiceAccount>>
) => {
  setter({ id: '', data: null, action: null });
};

const PermissionGroupsSelect: FC<{
  value: Option[];
  onChange: (groups: Option[]) => void;
}> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const { data: options = [] } = useQuery(
    permissionGroupsQuery({
      ...(debouncedSearch && {
        params: { search: debouncedSearch },
      }),
      placeholderData: keepPreviousData,
      staleTime: Infinity,
      select: (data) => {
        return data.map((pg) => {
          return { value: pg.id, label: pg.name };
        });
      },
    })
  );

  useDebounce(
    () => {
      setDebouncedSearch(search);
    },
    DEFAULT_INPUT_DELAY,
    [search]
  );

  return (
    <MultiSelect
      value={value}
      inputValue={search}
      label={t('Permission Groups')}
      placeholder={t('Find Permission Groups')}
      options={options}
      onInputChange={(v: string, meta: { action: string }) => {
        if (meta.action === 'input-change') {
          setSearch(v);
        }
        if (meta.action === 'menu-close') {
          setSearch('');
        }
      }}
      onChange={onChange}
      portal
    />
  );
};

export const CreateServiceAccountModal: FC<CreateModalProps> = ({
  setSelectedServiceAccount,
  refetch,
}) => {
  const { t } = useTranslation();
  const [showToast] = useToast();
  const [name, setName] = useState('');
  const [permissionGroups, setPermissionGroups] = useState<Option[]>([]);
  const [nameError, setNameError] = useState<string | undefined>();
  const [showNoGroupsConfirm, setShowNoGroupsConfirm] = useState(false);

  const [message, showMessage, flashErrorMessage, cancelFlash] =
    useFlashTransition();

  const handleHide = useCallback(() => {
    closeModal(setSelectedServiceAccount);
  }, [setSelectedServiceAccount]);

  const { mutate, status } = useMutation({
    mutationFn: () =>
      ServiceAccountService.createServiceAccount({
        name,
        permission_groups: permissionGroups.map(({ value }) => value),
      }),
    onSuccess: () => {
      refetch();
      handleHide();
    },
    onError: (error: AxiosError<any>) => {
      const axiosError = getOriginalError(error);
      if (axiosError?.name) {
        setNameError(axiosError.name[0]);
      } else {
        showToast({
          message:
            getErrorMessage(axiosError) ??
            t('Service account could not be created. Please try again.'),
          variant: toastVariant.FAILURE,
        });
      }
    },
  });

  const handleConfirm = useCallback(() => {
    if (!name.trim()) {
      setNameError(t('Name is required'));
      return;
    }
    if (permissionGroups.length === 0) {
      setShowNoGroupsConfirm(true);
      return;
    }
    mutate();
  }, [name, permissionGroups, mutate, t]);

  useEffect(() => {
    if (nameError) {
      flashErrorMessage(nameError);
    }
    return () => {
      cancelFlash();
    };
  }, [nameError, flashErrorMessage, cancelFlash]);

  return (
    <>
      <BasicModal
        heading={t('New Service Account')}
        onConfirm={handleConfirm}
        buttonText={t('Save')}
        onHide={handleHide}
        show
        size="small"
        disabled={status === 'pending'}
      >
        <TextInput
          value={name}
          onChange={(v: string) => {
            setName(v);
            setNameError(undefined);
            cancelFlash();
          }}
          label={t('Name')}
          placeholder={t('Enter Name')}
          error={nameError}
          inModal
          data-qa="service-account-name-input"
          margin
          validate={
            nameError
              ? {
                  message,
                  showMessage,
                }
              : undefined
          }
        />
        <PermissionGroupsSelect
          value={permissionGroups}
          onChange={setPermissionGroups}
        />
      </BasicModal>
      <ConfirmationModal
        show={showNoGroupsConfirm}
        heading={t('No Permission Groups')}
        buttonText={t('Proceed')}
        onConfirm={() => {
          setShowNoGroupsConfirm(false);
          mutate();
        }}
        onHide={() => setShowNoGroupsConfirm(false)}
      >
        <KizenTypography>
          {t(
            "Without any permission groups, integrations using this service account's API key will not work. Are you sure?"
          )}
        </KizenTypography>
      </ConfirmationModal>
    </>
  );
};

export const ViewApiTokenModal: FC<ViewModalProps> = ({
  selectedServiceAccount,
  setSelectedServiceAccount,
}) => {
  const { t } = useTranslation();

  const chosenBusiness = useSelector(getChosenBusiness);
  const hasSecondary =
    !!selectedServiceAccount.data?.secondary_api_token_preview;

  const handleHide = useCallback(() => {
    closeModal(setSelectedServiceAccount);
  }, [setSelectedServiceAccount]);

  const {
    data: primaryToken,
    isFetching: isPrimaryLoading,
    error: primaryTokenError,
  } = useQuery(
    primaryTokenQuery(selectedServiceAccount.id, {
      enabled: !!selectedServiceAccount.id,
      throwOnError: false,
    })
  );

  const {
    data: secondaryToken,
    isFetching: isSecondaryLoading,
    error: secondaryTokenError,
  } = useQuery(
    secondaryTokenQuery(selectedServiceAccount.id, {
      enabled: !!selectedServiceAccount.id,
      throwOnError: false,
    })
  );

  return (
    <BasicModal
      heading={t('API Keys')}
      onConfirm={handleHide}
      buttonText={t('Close')}
      actionBtnColor="blue"
      leftBtn={null}
      onHide={handleHide}
      show
      size="small"
      disabled={isPrimaryLoading || isSecondaryLoading}
    >
      <Loader loading={isPrimaryLoading || isSecondaryLoading}>
        <TextInput
          value={(!primaryTokenError && primaryToken?.key) || emptyLongDash}
          label={t('Primary API Key')}
          disabled
          margin
          data-qa="primary-api-token"
        />
        {(hasSecondary || secondaryToken) && !secondaryTokenError ? (
          <TextInput
            value={secondaryToken?.key ?? emptyLongDash}
            label={t('Secondary API Key')}
            disabled
            margin
            data-qa="secondary-api-token"
          />
        ) : null}
        <TextInput
          value={primaryToken?.employee?.user_id ?? emptyLongDash}
          label={t('User ID')}
          disabled
          margin
          data-qa="user-id"
        />
        <TextInput
          value={chosenBusiness?.id ?? emptyLongDash}
          label={t('Business ID')}
          disabled
          margin
          data-qa="business-id"
        />
      </Loader>
    </BasicModal>
  );
};

export const RotateTokenModal: FC<ActionModalProps> = ({
  selectedServiceAccount,
  setSelectedServiceAccount,
  refetch,
}) => {
  const { t } = useTranslation();
  const [showToast] = useToast();
  const { data } = selectedServiceAccount;
  const hasSecondary = !!data?.secondary_api_token_preview;

  const handleHide = useCallback(() => {
    closeModal(setSelectedServiceAccount);
  }, [setSelectedServiceAccount]);

  const queryClient = useQueryClient();

  const { mutate, status } = useMutation({
    mutationFn: () =>
      ServiceAccountService.rotateServiceAccountToken(
        selectedServiceAccount.id
      ),
    onSuccess: (updatedAccount) => {
      refetch();
      queryClient.invalidateQueries({
        queryKey: primaryTokenQuery(selectedServiceAccount.id).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: secondaryTokenQuery(selectedServiceAccount.id).queryKey,
      });
      setSelectedServiceAccount({
        id: selectedServiceAccount.id,
        action: 'view-token',
        data: selectedServiceAccount.data,
      });
    },
    onError: (error: AxiosError) => {
      showToast({
        message:
          getErrorMessage(getOriginalError(error)) ??
          t('Failed to rotate key. Please try again.'),
        variant: toastVariant.FAILURE,
      });
    },
  });

  const body = hasSecondary
    ? t(
        'The secondary key ({{token}}) for this service account will be deleted. The current primary API key will remain active until you delete it or rotate keys again. Are you sure?',
        { token: data?.secondary_api_token_preview }
      )
    : t(
        'The current primary API key for this service account will remain active until you delete it or rotate keys again. Are you sure?'
      );

  return (
    <ConfirmationModal
      show
      heading={t('Rotate API Key')}
      buttonText={t('Proceed')}
      onConfirm={() => mutate()}
      onHide={handleHide}
      disabled={status === 'pending'}
    >
      <KizenTypography>{body}</KizenTypography>
    </ConfirmationModal>
  );
};

export const DeleteSecondaryTokenModal: FC<ActionModalProps> = ({
  selectedServiceAccount,
  setSelectedServiceAccount,
  refetch,
}) => {
  const { t } = useTranslation();
  const [showToast] = useToast();
  const { data } = selectedServiceAccount;

  const queryClient = useQueryClient();

  const handleHide = useCallback(() => {
    closeModal(setSelectedServiceAccount);
  }, [setSelectedServiceAccount]);

  const { mutate, status } = useMutation({
    mutationFn: () =>
      ServiceAccountService.deleteSecondaryToken(selectedServiceAccount.id),
    onSuccess: () => {
      refetch();
      queryClient.invalidateQueries({
        queryKey: secondaryTokenQuery(selectedServiceAccount.id).queryKey,
      });
      handleHide();
    },
    onError: (error: AxiosError) => {
      showToast({
        message:
          getErrorMessage(getOriginalError(error)) ??
          t('Failed to delete secondary key. Please try again.'),
        variant: toastVariant.FAILURE,
      });
    },
  });

  return (
    <ConfirmationModal
      show
      heading={t('Delete Secondary API Key')}
      buttonText={t('Delete')}
      onConfirm={() => mutate()}
      onHide={handleHide}
      disabled={status === 'pending'}
    >
      <KizenTypography>
        {t(
          'Integrations using this API key ({{token}}) will not work until their API key is updated. Are you sure?',
          { token: data?.secondary_api_token_preview ?? '' }
        )}
      </KizenTypography>
    </ConfirmationModal>
  );
};

// KZN-17001: Add support for 'disabled' status for service accounts

// export const DisableServiceAccountModal: FC<ActionModalProps> = ({
//   selectedServiceAccount,
//   setSelectedServiceAccount,
//   refetch,
// }) => {
//   const { t } = useTranslation();
//   const [showToast] = useToast();
//
//   const handleHide = useCallback(() => {
//     closeModal(setSelectedServiceAccount);
//   }, [setSelectedServiceAccount]);
//
//   const { mutate, status } = useMutation({
//     mutationFn: () => ServiceAccountService.disable(selectedServiceAccount.id),
//     onSuccess: () => {
//       refetch();
//       handleHide();
//     },
//     onError: (error: AxiosError) => {
//       showToast({
//         message:
//           getErrorMessage(getOriginalError(error)) ??
//           t('Failed to disable service account. Please try again.'),
//         variant: toastVariant.FAILURE,
//       });
//     },
//   });
//
//   return (
//     <ConfirmationModal
//       show
//       heading={t('Disable Service Account')}
//       buttonText={t('Disable')}
//       onConfirm={() => mutate()}
//       onHide={handleHide}
//       disabled={status === 'pending'}
//     >
//       <KizenTypography>
//         {t(
//           "Integrations using this service account's API key will not work while the account is disabled. Are you sure?"
//         )}
//       </KizenTypography>
//     </ConfirmationModal>
//   );
// };

// export const EnableServiceAccountModal: FC<ActionModalProps> = ({
//   selectedServiceAccount,
//   setSelectedServiceAccount,
//   refetch,
// }) => {
//   const { t } = useTranslation();
//   const [showToast] = useToast();
//
//   const handleHide = useCallback(() => {
//     closeModal(setSelectedServiceAccount);
//   }, [setSelectedServiceAccount]);
//
//   const { mutate, status } = useMutation({
//     mutationFn: () => ServiceAccountService.enable(selectedServiceAccount.id),
//     onSuccess: () => {
//       refetch();
//       handleHide();
//     },
//     onError: (error: AxiosError) => {
//       showToast({
//         message:
//           getErrorMessage(getOriginalError(error)) ??
//           t('Failed to enable service account. Please try again.'),
//         variant: toastVariant.FAILURE,
//       });
//     },
//   });
//
//   return (
//     <ConfirmationModal
//       show
//       heading={t('Enable Service Account')}
//       buttonText={t('Enable')}
//       onConfirm={() => mutate()}
//       onHide={handleHide}
//       disabled={status === 'pending'}
//     >
//       <KizenTypography>
//         {t('Are you sure you want to enable this service account?')}
//       </KizenTypography>
//     </ConfirmationModal>
//   );
// };

const Ellipsis: FC<PropsWithChildren> = ({ children }) => (
  <StyledAccountName>
    <TextEllipsisWithTooltip as="span">{children}</TextEllipsisWithTooltip>
  </StyledAccountName>
);

export const ArchiveServiceAccountModal: FC<ActionModalProps> = ({
  selectedServiceAccount,
  setSelectedServiceAccount,
  refetch,
}) => {
  const { t } = useTranslation();
  const [showToast] = useToast();
  const [typedName, setTypedName] = useState('');
  const { data } = selectedServiceAccount;

  const nameMatches =
    typedName.trim().toLowerCase() === data?.name?.trim().toLowerCase();

  const handleHide = useCallback(() => {
    setTypedName('');
    closeModal(setSelectedServiceAccount);
  }, [setSelectedServiceAccount]);

  const { mutate, status } = useMutation({
    mutationFn: () =>
      ServiceAccountService.archiveServiceAccount(selectedServiceAccount.id),
    onSuccess: () => {
      refetch();
      handleHide();
    },
    onError: (error: AxiosError) => {
      showToast({
        message:
          getErrorMessage(getOriginalError(error)) ??
          t('Failed to archive service account. Please try again.'),
        variant: toastVariant.FAILURE,
      });
    },
  });

  return (
    <BasicModal
      heading={t('Please Confirm Archiving')}
      buttonText={t('Archive')}
      onConfirm={() => mutate()}
      onHide={handleHide}
      show
      size="small"
      disabled={!nameMatches || status === 'pending'}
      data-qa="archive-service-account-modal"
    >
      <StyledDeletionNotice>
        <Trans
          i18nKey="Integrations using this service account's API key will no longer work. This action is not reversible. Type in the name of this service account <ellipsis>({{name}})</ellipsis> to proceed."
          values={{
            name: data?.name,
          }}
          components={{
            ellipsis: <Ellipsis />,
          }}
        />
      </StyledDeletionNotice>
      <TextInput
        value={typedName}
        onChange={setTypedName}
        placeholder={t('Type account name to confirm')}
        inModal
        data-qa="archive-confirm-name-input"
      />
    </BasicModal>
  );
};
